<div class="bg-white rounded-3xl h-full w-full p-6 flex flex-col gap-6">
  <div class="h-full w-full overflow-auto p-2">
    <p class="text-xl oswald-font font-medium text-gray-500 pb-4">Fleet</p>

    <div
      *ngIf="!selectedBuilding()"
      class="bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5 text-xs text-amber-800 mb-4">
      Select a building from the top navbar to continue.
    </div>

    <div *ngIf="selectedBuilding()" class="flex flex-col gap-4">
      <div class="flex flex-col gap-3">
        <div class="flex items-center gap-2">
          <span class="text-xs uppercase tracking-wide text-gray-400 oswald-font w-32 shrink-0">
            Trackers
          </span>
          <button
            type="button"
            (click)="selectTab('trackers')"
            [ngClass]="
              activeTab() === 'trackers'
                ? 'bg-blue-500 text-white border-blue-500'
                : 'bg-white hover:bg-blue-100 text-gray-700 border-gray-300'
            "
            class="flex items-center gap-2 px-4 py-1.5 rounded-md text-sm oswald-font border transition-colors">
            <lucide-icon [img]="icons.Cpu" [size]="16"></lucide-icon>
            All Trackers
          </button>
        </div>
        
        <div class="flex items-start gap-2">
          <span class="text-xs uppercase tracking-wide text-gray-400 oswald-font w-32 shrink-0 pt-2">
            Transportation Levels
          </span>
          <div class="flex flex-wrap gap-2">
            <span
              *ngIf="!loadingLevels() && levels().length === 0"
              class="text-xs text-gray-500 italic py-1.5">
              No live levels.
            </span>
            <button
              *ngFor="let lvl of levels()"
              type="button"
              (click)="selectTab(lvl.id)"
              [ngClass]="
                activeTab() === lvl.id
                  ? 'bg-indigo-500 text-white border-indigo-500'
                  : 'bg-white hover:bg-indigo-50 text-gray-700 border-gray-300'
              "
              class="px-3 py-1.5 rounded-md text-sm oswald-font border transition-colors">
              {{ lvl.name }}
            </button>
          </div>
        </div>
      </div>

      <!-- =================================================================
           All Trackers pane — agent rows with which layer each one feeds.
           ================================================================= -->
      <div *ngIf="activeTab() === 'trackers'" class="flex flex-col gap-4">
        <div class="flex items-center justify-between border-t border-gray-200 pt-3">
          <p class="oswald-font text-lg font-medium text-gray-700">All Trackers</p>
          <button
            type="button"
            (click)="openCreateAgent()"
            class="flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-500 hover:bg-blue-600 text-white text-sm oswald-font">
            <lucide-icon [img]="icons.Plus" [size]="14"></lucide-icon>
            Add Tracker
          </button>
        </div>

        <div
          *ngIf="!loadingAgents() && agents().length === 0"
          class="text-xs text-gray-500 italic">
          No trackers for this building.
        </div>

        <div
          *ngIf="agents().length > 0"
          class="w-full overflow-x-auto border border-gray-300 rounded-md">
          <table class="w-full text-xs">
            <thead class="bg-blue-50">
              <tr class="text-left">
                <th class="px-3 py-2">Unit ID</th>
                <th class="px-3 py-2">Tracker Name</th>
                <th class="px-3 py-2">Layer Name</th>
                <th class="px-3 py-2">Fleet Version</th>
                <th class="px-3 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                *ngFor="let agent of agents()"
                class="border-t border-gray-200 hover:bg-gray-50">
                <td class="px-3 py-1.5 break-all">{{ agent.id }}</td>
                <td class="px-3 py-1.5">{{ agent.name || '—' }}</td>
                <td class="px-3 py-1.5">
                  <ng-container *ngIf="layerNamesForAgent(agent.id) as names">
                    <span *ngIf="names.length > 0">{{ names.join(', ') }}</span>
                    <span *ngIf="names.length === 0" class="text-gray-400">—</span>
                  </ng-container>
                </td>
                <td class="px-3 py-1.5">
                  <span
                    *ngIf="agent.fleetVersion != null; else fvUnset"
                    class="text-gray-700">
                    {{ fleetVersionLabel(agent.fleetVersion) }}
                  </span>
                  <ng-template #fvUnset><span class="text-gray-400">—</span></ng-template>
                </td>
                <td class="px-3 py-1.5 text-right">
                  <div class="flex justify-end gap-2">
                    <a
                      [routerLink]="['/fleet-reports', agent.id]"
                      title="View reports"
                      class="text-gray-600 hover:text-indigo-700">
                      <lucide-icon [img]="icons.BarChart3" [size]="16"></lucide-icon>
                    </a>
                    <button
                      *ngIf="agent.fleetVersion === 2"
                      type="button"
                      (click)="requestGpsData(agent)"
                      [disabled]="requestingGpsFor().has(agent.id)"
                      title="Request current GPS position now"
                      class="text-gray-600 hover:text-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed">
                      <lucide-icon [img]="icons.Radio" [size]="16"></lucide-icon>
                    </button>
                    <button
                      type="button"
                      (click)="openEditAgent(agent)"
                      title="Edit tracker"
                      class="text-gray-600 hover:text-indigo-700">
                      <lucide-icon [img]="icons.Pencil" [size]="16"></lucide-icon>
                    </button>
                    <button
                      type="button"
                      (click)="deleteAgent(agent)"
                      title="Delete tracker"
                      class="text-red-600 hover:text-red-800">
                      <lucide-icon [img]="icons.Trash2" [size]="16"></lucide-icon>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          *ngIf="!loadingLevels() && levels().length === 0"
          class="text-xs text-gray-500 italic">
          This building has no live levels.
        </div>
      </div>

      <!-- =================================================================
           Level pane — layer rows with which tracker (if any) feeds them.
           ================================================================= -->
      <div *ngIf="activeLevelId() != null" class="flex flex-col gap-4">
        <div class="flex items-center justify-between border-t border-gray-200 pt-3">
          <p class="oswald-font text-lg font-medium text-gray-700">
            Layers on this level
          </p>
          <button
            type="button"
            (click)="openAddLayer()"
            class="flex items-center gap-1 px-3 py-1.5 rounded-md bg-indigo-500 hover:bg-indigo-600 text-white text-sm oswald-font">
            <lucide-icon [img]="icons.Plus" [size]="14"></lucide-icon>
            Add Layer
          </button>
        </div>

        <div
          *ngIf="
            loadingLayersForLevelId() !== activeLevelId() &&
            layersForSelectedLevel().length === 0
          "
          class="text-xs text-gray-500 italic">
          No layers are mapped to this level.
        </div>

        <div
          *ngIf="layersForSelectedLevel().length > 0"
          class="w-full overflow-x-auto border border-gray-300 rounded-md">
          <table class="w-full text-xs">
            <thead class="bg-indigo-100">
              <tr class="text-left">
                <th class="px-3 py-2">Layer ID</th>
                <th class="px-3 py-2">Layer Name</th>
                <th class="px-3 py-2">Tracker ID</th>
                <th class="px-3 py-2">Tracker Name</th>
                <th class="px-3 py-2 w-16"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                *ngFor="let layer of layersForSelectedLevel()"
                class="border-t border-gray-200 hover:bg-gray-50">
                <td class="px-3 py-1.5 tabular-nums">{{ layer.id }}</td>
                <td class="px-3 py-1.5">{{ layer.name || '—' }}</td>
                <td class="px-3 py-1.5 text-gray-600 break-all">
                  {{ layer.agentId || '—' }}
                </td>
                <td class="px-3 py-1.5">
                  {{ layer.agentId ? (agentNameForId(layer.agentId) || '—') : '—' }}
                </td>
                <td class="px-3 py-1.5 text-right">
                  <div class="flex justify-end gap-2">
                    <button
                      type="button"
                      (click)="openEditLayer(layer)"
                      title="Edit layer (assign tracker)"
                      class="text-gray-600 hover:text-indigo-700">
                      <lucide-icon [img]="icons.Pencil" [size]="16"></lucide-icon>
                    </button>
                    <button
                      type="button"
                      (click)="deleteLayer(layer)"
                      title="Delete layer"
                      class="text-red-600 hover:text-red-800">
                      <lucide-icon [img]="icons.Trash2" [size]="16"></lucide-icon>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- ============================================================
     Modals — each owns its form internals; the parent only wires
     them up via @Input/@Output to the API call methods above.
     ============================================================ -->

<tracker-modal
  [mode]="trackerModalMode()"
  [open]="trackerModalOpen()"
  [saving]="trackerModalSaving()"
  [initialAgent]="currentEditAgent()"
  (save)="onTrackerModalSave($event)"
  (closeModal)="closeTrackerModal()">
</tracker-modal>

<layer-modal
  [mode]="layerModalMode()"
  [open]="layerModalOpen()"
  [saving]="layerModalSaving()"
  [loading]="layerModalLoading()"
  [assignableAgents]="assignableAgents()"
  [initialLayer]="currentEditLayer()"
  [styles]="layerStyles()"
  [loadingStyles]="loadingLayerStyles()"
  (save)="onLayerModalSave($event)"
  (closeModal)="closeLayerModal()"
  (addStyle)="onAddStyleFromLayer()"
  (editStyle)="onEditStyleFromLayer($event)"
  (deleteStyle)="onDeleteStyleFromLayer($event)">
</layer-modal>

<style-modal
  [mode]="styleModalMode()"
  [open]="styleModalOpen()"
  [saving]="styleModalSaving()"
  [initialStyle]="currentEditStyle()"
  [icons$list]="layerIcons()"
  [iconsLoading]="loadingLayerIcons()"
  [iconUploading]="iconUploading()"
  [rules]="styleRules()"
  [actions]="styleActions()"
  [ruleSaving]="ruleSaving()"
  [actionSaving]="actionSaving()"
  [geofences]="geofences()"
  [geofencesLoading]="geofencesLoading()"
  [actionUsers]="actionUsers()"
  [actionUsersLoading]="actionUsersLoading()"
  (save)="onStyleModalSave($event)"
  (closeModal)="closeStyleModal()"
  (iconFileChosen)="onStyleIconFileChosen($event)"
  (addRule)="onAddRule($event)"
  (deleteRule)="onDeleteRule($event)"
  (addAction)="onAddAction($event)"
  (deleteAction)="onDeleteAction($event)">
</style-modal>

<!-- Destructive-action confirmation — generic, used by every Delete handler. -->
<div
  *ngIf="confirmState() as c"
  class="fixed inset-0 flex items-center justify-center bg-main-background bg-opacity-75 z-40 p-4">
  <div
    class="p-8 rounded-md bg-white/95 shadow-xl border border-gray-200 min-w-[360px] max-w-[480px]">
    <div class="flex flex-col items-center text-center gap-4">
      <div class="text-red-500">
        <lucide-icon [img]="icons.CircleAlert" [size]="40"></lucide-icon>
      </div>
      <p class="oswald-font text-lg text-gray-800">{{ c.title }}</p>
      <p class="text-sm text-gray-600 whitespace-pre-line">{{ c.message }}</p>

      <div class="flex items-center gap-3 pt-2">
        <button
          type="button"
          (click)="closeConfirm()"
          [disabled]="c.confirmRunning"
          class="px-5 py-2 rounded-md border border-gray-400 oswald-font hover:bg-gray-50 disabled:opacity-60">
          Cancel
        </button>
        <button
          type="button"
          (click)="runConfirm()"
          [disabled]="c.confirmRunning"
          class="px-6 py-2 rounded-md bg-gradient-to-r from-rose-500 via-red-500 to-orange-500 hover:from-rose-600 hover:via-red-600 hover:to-orange-600 text-white oswald-font disabled:opacity-60 disabled:cursor-not-allowed">
          <ng-container *ngIf="!c.confirmRunning">{{ c.confirmLabel }}</ng-container>
          <ng-container *ngIf="c.confirmRunning">Deleting…</ng-container>
        </button>
      </div>
    </div>
  </div>
</div>

<spinner *ngIf="fetching()" pageName="Fleet"></spinner>
